import { camelCase, upperCase } from 'lodash-es';

import type { InteractiveTitleSettingDefinition } from '../types';

export type TitleToken =
    | {
          type: 'text';
          text: string;
      }
    | {
          type: 'replacement';
          text: string;
          label: string;
          value: string | null;
      }
    | {
          type: 'interactive';
          text: string;
          label: string;
          value: InteractiveTitleSettingDefinition | null;
      };

export const TitleToken = {
    toString: (tokens: TitleToken[]): string => {
        return tokens
            .map(token =>
                token.type === 'replacement' && token.value !== null
                    ? token.value
                    : token.type === 'interactive' && token.value !== null
                    ? token.value.field.value
                    : token.text,
            )
            .join('');
    },
};

export function titleParser(
    title: string,
    replacements: Record<string, string>,
    interactives: Record<string, InteractiveTitleSettingDefinition>,
): TitleToken[] {
    const replacementTagRegex = /<[\w.]+>/; // dot character is used when accessing specific levels of hierarchy filters
    const interactiveTagRegex = /<<\w+>>/;
    const regexes = [interactiveTagRegex, replacementTagRegex].map(r => r.source);
    const capturingRegexes = regexes.map(s => `(${s})`);
    const splitterRegex = new RegExp(`(?=${regexes.join('|')})${capturingRegexes.join('|')}`);

    return title
        .split(splitterRegex)
        .filter(Boolean)
        .map(token => {
            if (interactiveTagRegex.test(token)) {
                const label = token.replace('<<', '').replace('>>', '');
                const value =
                    interactives[label] ??
                    interactives[camelCase(label)] ??
                    interactives[pascalCase(label)] ??
                    interactives[pascalCaseWithoutWhitespace(label)] ??
                    null;

                return {
                    type: 'interactive',
                    text: token,
                    label,
                    value,
                };
            }

            if (replacementTagRegex.test(token)) {
                const label = token.replace('<', '').replace('>', '');
                const value =
                    replacements[label] ??
                    replacements[camelCase(label)] ??
                    replacements[pascalCase(label)] ??
                    replacements[pascalCaseWithoutWhitespace(label)] ??
                    null;

                return {
                    type: 'replacement',
                    text: token,
                    label,
                    value,
                };
            }
            return { type: 'text', text: token };
        });
}

function pascalCase(text: string) {
    return upperCase(text).replace(/\s/g, '_');
}
function pascalCaseWithoutWhitespace(text: string) {
    return upperCase(text).replace(/\s/g, '');
}
